import { store } from '../store'
import { datadogLogs } from '@datadog/browser-logs'
import { getEnvironment } from './awsUtils'

export const initLogger = () => {
  datadogLogs.init({
    clientToken: 'pubf1692edee7a0c0df7d71ec15fc34bf08',
    site: 'datadoghq.com',
    service: 'providers-webapp',
    env: getEnvironment(),
    version: process.env.REACT_APP_VERSION,
    forwardErrorsToLogs: true,
    sampleRate: 100
  })
}

export const logInfo = (message, context) => {
  log(message, context, 'info')
}

export const logWarn = (message, context) => {
  log(message, context, 'warn')
}

export const logError = (message, error) => {
  log(message, error, 'error')
}

export const logReduxActionError = (action, context) => {
  log(
    `Redux action: ${action.type}`,
    { ...(context || {}), actionType: action.type, actionPayload: action.payload },
    'error'
  )
}

export const log = (logMessage, data, level) => {
  const state = store.getState()

  const context = {
    ...(data || {}),
    env: getEnvironment(),
    webappLoadTime: state.appReducer.loadedAt,
    userInfo: {
      username: state.profileReducer.doctor?.username,
      doctorId: state.profileReducer.doctor?.id,
      email: state.profileReducer.doctor?.email
    }
  }

  if (getEnvironment() === 'production' || getEnvironment() === 'staging') {
    datadogLogs.logger[level](logMessage, context)
  }
  if (getEnvironment() !== 'production') {
    console[level](logMessage, context)
  }
}

export const getSessionId = () => datadogLogs.getInternalContext().session_id

// Query-string keys preserved by sanitizePathname. Mirrors the allowlist used by
// chat-service so client and server agree on what ends up in log aggregators.
const MARK_READ_PATHNAME_ALLOWED_QS_KEYS = ['patientView', 'tab']
const MARK_READ_PATHNAME_MAX_LENGTH = 2048

/**
 * Strips all query-string keys except a small allowlist and caps the length.
 * Returns undefined on non-string input, if the input exceeds the cap, or if
 * anything throws while parsing. Mirrors the server-side sanitizer so that
 * CloudWatch (chat-service) and Datadog (webapp) carry identical pathnames.
 */
export const sanitizePathname = pathname => {
  try {
    if (typeof pathname !== 'string') return undefined
    if (pathname.length > MARK_READ_PATHNAME_MAX_LENGTH) return undefined

    const queryIndex = pathname.indexOf('?')
    if (queryIndex === -1) return pathname

    const pathPart = pathname.slice(0, queryIndex)
    const queryPart = pathname.slice(queryIndex + 1)
    if (!queryPart) return pathPart

    const kept = queryPart
      .split('&')
      .filter(Boolean)
      .map(pair => {
        const eq = pair.indexOf('=')
        const key = eq === -1 ? pair : pair.slice(0, eq)
        return { pair, key: decodeURIComponent(key) }
      })
      .filter(({ key }) => MARK_READ_PATHNAME_ALLOWED_QS_KEYS.includes(key))
      .map(({ pair }) => pair)

    return kept.length ? `${pathPart}?${kept.join('&')}` : pathPart
  } catch (e) {
    return undefined
  }
}

/**
 * Reads a single query-string value from `window.location.search` without
 * throwing. Returns undefined when absent or when anything goes wrong.
 */
const readQueryParam = key => {
  try {
    if (typeof window === 'undefined' || !window.location) return undefined
    const params = new URLSearchParams(window.location.search || '')
    const value = params.get(key)
    return value == null ? undefined : value
  } catch (e) {
    return undefined
  }
}

const safeParseAppSettings = appSettings => {
  try {
    return JSON.parse(appSettings || '{}')
  } catch (e) {
    return {}
  }
}

/**
 * Builds the shared context object used by the three mark-read log events and
 * by the enriched POST body sent to chat-service. Missing fields become
 * undefined (Datadog drops undefined keys), so the caller never has to branch
 * on what is or isn't populated.
 */
export const buildMarkReadContext = ({ roomId, analytics, clientRequestId, state } = {}) => {
  const safeState = state || {}
  const accountOwnerAppSettings = safeParseAppSettings(safeState.practiceReducer?.accountOwner?.user?.appSettings)
  const currentUserAppSettings = safeParseAppSettings(safeState.profileReducer?.doctor?.user?.appSettings)

  let pathname
  try {
    if (typeof window !== 'undefined' && window.location) {
      pathname = sanitizePathname(`${window.location.pathname || ''}${window.location.search || ''}`)
    }
  } catch (e) {
    pathname = undefined
  }

  let sessionId
  try {
    sessionId = getSessionId()
  } catch (e) {
    sessionId = undefined
  }

  return {
    clientRequestId,
    roomId,
    patientId: analytics?.patientId,
    reason: analytics?.reason,
    sourceComponent: analytics?.sourceComponent,
    patientView: readQueryParam('patientView'),
    pathname,
    // `blueDotMode` is kept for backwards compatibility with existing log
    // consumers; the two *_blueDotMode fields below are the new, unambiguous
    // ones.
    blueDotMode: accountOwnerAppSettings.blueDotMode,
    accountOwnerBlueDotMode: accountOwnerAppSettings.blueDotMode,
    currentUserBlueDotMode: currentUserAppSettings.blueDotMode,
    sessionId
  }
}

/**
 * Emits a mark-read lifecycle event to Datadog browser logs. `_failed` events
 * are logged at error level; everything else is info. Any failure inside this
 * helper is swallowed so a logging problem can never break the epic.
 */
export const logMarkReadEvent = (eventName, context) => {
  try {
    const level = eventName === 'chat_room_mark_read_failed' ? 'error' : 'info'
    log(eventName, { event: eventName, ...(context || {}) }, level)
  } catch (e) {
    // Logging must never throw. Swallow and continue.
  }
}

export const circularReplacer = () => {
  const seen = new WeakSet()
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return
      }
      seen.add(value)
    }
    return value
  }
}
